/* eslint-disable react/no-unused-prop-types */
/**
 * ReviewRightPanel
 * ----------------
 * Right sidebar for Screen 3 (Review). Cash App styling.
 *
 * Save strategy:
 * - StatusSelect: saves via its own setIntakeStatus endpoint
 * - ManualQualificationControl: saves via its own updateIntake call
 * - Lead/Referral source: direct PATCH via updateIntake (no stale state race)
 * - Firm/Assignment: direct PATCH via updateIntake
 * - StatusTrackingPanel: has its own save logic for LOE/attorney decision
 * - ScheduleCallbackPanel: has its own mutation
 * - Notes: uses addGeneralNote → updateIntakeNotesLog endpoint
 */

import {
  Archive,
  CheckCircle2,
  ChevronDown,
  Download,
  Eye,
  FileText,
  HelpCircle,
  Link2,
  Loader2,
  MessageSquare,
  Send,
  Sparkles,
  Star,
  Users,
  XCircle,
  Zap,
} from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { AutoChaseCard } from '@/components/intakes/components/AutoChaseCard';
import { HandlerToggle } from '@/components/intakes/components/HandlerToggle';
import { ScheduleCallbackPanel } from '@/components/intakes/components/ScheduleCallbackPanel';
import { StatusSelect } from '@/components/intakes/components/StatusSelect';
import StatusTrackingPanel from '@/components/intakes/components/StatusTrackingPanel';
import { REFERRAL_SOURCES } from '@/components/intakes/constants';
import { DocumentsPanel } from '@/components/intakes/DocumentsPanel';
import { mergeFormDataPatchWithRepairs } from '@/components/intakes/utils/formDataSanitizers';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useIntakePage } from '@/hooks/useIntakePage';
import { cn } from '@/lib/utils';
import { useGetBlobsQuery } from '@/services/api/blobService';
import { useLazyGetPortalLinkQuery } from '@/services/api/clientPortalService';
import { useGetFirmsQuery } from '@/services/api/firmsService';
import {
  useCancelScheduleMutation,
  useGenerateIntakeSummaryMutation,
  useGetIntakeMetadataQuery,
  useScheduleCallbackMutation,
  useUpdateIntakeMutation,
} from '@/services/api/intakeService';
import { useGetStaffUsersQuery } from '@/services/api/userService';
import type { Firm } from '@/services/types/firms-types';
import { ManualQualificationEnum, ScheduleCallbackPayload } from '@/services/types/intake-types';

import { useIntakeFormData } from './IntakeFormDataContext';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const labelCls = 'text-[10px] font-black text-zinc-400 uppercase tracking-widest';

// ---------------------------------------------------------------------------
// Docs Header — download all + file count
// ---------------------------------------------------------------------------

const DocsHeader: React.FC<{ matterId: string }> = ({ matterId }) => {
  const { data: blobs = [] } = useGetBlobsQuery(matterId);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadAll = useCallback(async () => {
    if (blobs.length === 0) return;
    setIsDownloading(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Fetch all files in parallel
      const results = await Promise.allSettled(
        blobs.map(async (blob: { name: string; download_url: string }) => {
          const response = await fetch(blob.download_url);
          if (!response.ok) throw new Error(`Failed to fetch ${blob.name}`);
          const data = await response.blob();
          zip.file(blob.name, data);
        }),
      );

      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        toast.error(`${failed} file(s) failed to download`);
      }

      // Generate zip and trigger download
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `intake-documents-${matterId.slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to create zip file');
    } finally {
      setIsDownloading(false);
    }
  }, [blobs, matterId]);

  return (
    <div className="flex items-center justify-between px-1">
      <div className="flex items-center gap-1.5">
        <span className={labelCls}>Documents</span>
        {blobs.length > 0 && (
          <span className="text-[10px] font-bold text-zinc-400">{blobs.length}</span>
        )}
      </div>
      {blobs.length > 0 && (
        <button
          type="button"
          onClick={handleDownloadAll}
          disabled={isDownloading}
          className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold text-zinc-500 hover:text-zinc-900 hover:bg-white transition-all disabled:opacity-50"
        >
          <Download className="size-3" />
          {isDownloading ? 'Downloading...' : 'Download All'}
        </button>
      )}
    </div>
  );
};

// Shared CSS override wrapper for legacy components (StatusTrackingPanel, ScheduleCallbackPanel)
const cashAppOverrideClasses = cn(
  '[&>div]:!bg-white [&>div]:!rounded-2xl [&>div]:!border-zinc-200 [&>div]:!shadow-none',
  '[&_button.justify-between]:!px-3 [&_button.justify-between]:!py-2.5',
  '[&_button.justify-between_.text-sm]:!text-[10px] [&_button.justify-between_.text-sm]:!font-black [&_button.justify-between_.text-sm]:!text-zinc-400 [&_button.justify-between_.text-sm]:!uppercase [&_button.justify-between_.text-sm]:!tracking-widest',
  '[&_button.justify-between_svg]:!size-3.5 [&_button.justify-between_svg]:!text-zinc-400',
  '[&_.bg-blue-50]:!bg-zinc-50 [&_.bg-blue-50]:!border-zinc-200 [&_.text-blue-900]:!text-zinc-900 [&_.text-blue-700]:!text-zinc-500 [&_.bg-blue-200]:!bg-zinc-200 [&_.bg-blue-600]:!bg-zinc-900',
  '[&_.bg-gray-50]:!bg-zinc-50 [&_.bg-gray-50]:!border-zinc-200 [&_.bg-orange-50]:!bg-zinc-50 [&_.bg-orange-50]:!border-zinc-200',
  '[&_.text-gray-800]:!text-zinc-900 [&_.text-orange-800]:!text-zinc-900 [&_.text-gray-600]:!text-zinc-500 [&_.text-orange-600]:!text-zinc-500 [&_.text-gray-700]:!text-zinc-500',
  '[&_select]:!text-[11px] [&_select]:!font-bold [&_select]:!rounded-lg [&_select]:!border-zinc-200 [&_select]:!bg-white',
  '[&_textarea]:!text-[11px] [&_textarea]:!font-semibold [&_textarea]:!rounded-lg [&_textarea]:!border-zinc-200',
  '[&_input]:!text-[11px] [&_input]:!font-semibold [&_input]:!rounded-lg [&_input]:!border-zinc-200',
  '[&_.space-y-1_button]:!rounded-xl [&_.space-y-1_button]:!text-[10px] [&_.space-y-1_button]:!font-black [&_.space-y-1_button]:!uppercase [&_.space-y-1_button]:!tracking-wider',
  '[&_.grid_button]:!rounded-xl [&_.grid_button]:!text-[10px] [&_.grid_button]:!font-bold',
);

const cssOverride = (children: React.ReactNode) => (
  <div className={cashAppOverrideClasses}>{children}</div>
);

const LEAD_SOURCE_OPTIONS = [
  { value: 'Inbound Phone Call', label: 'Inbound Phone Call' },
  { value: 'Email - Facebook Lead', label: 'Facebook Lead' },
  { value: 'Email - Webform', label: 'Webform' },
  { value: 'Email - LSA', label: 'LSA' },
  { value: 'Email - Attorney Provided', label: 'Attorney Provided' },
  { value: 'Text', label: 'Text' },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReviewRightPanelProps {
  intakeId: string;
  firmId: string;
  onSendEngagementLetter?: () => void;
  onRefetch?: () => void;
}

// ---------------------------------------------------------------------------
// Pill Select — Cash App style dropdown
// ---------------------------------------------------------------------------

interface PillSelectOption {
  value: string;
  label: string;
  group?: string;
}

const PillSelect: React.FC<{
  value: string;
  options: PillSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  searchable?: boolean;
}> = ({ value, options, onChange, placeholder, searchable = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedLabel = options.find((o) => o.value === value)?.label || value;

  // Filter by search
  const filtered = search
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  // Group filtered options
  const groups = new Map<string, PillSelectOption[]>();
  filtered.forEach((opt) => {
    const g = opt.group || '';
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)?.push(opt);
  });

  const handleOpen = () => {
    setIsOpen(true);
    setSearch('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // Close only when focus leaves the entire container (button + dropdown + search)
  const handleBlur = useCallback((e: React.FocusEvent) => {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setIsOpen(false);
    }
  }, []);

  return (
    <div className="relative" ref={containerRef} onBlur={handleBlur}>
      <button
        type="button"
        onClick={() => isOpen ? setIsOpen(false) : handleOpen()}
        className={cn(
          'flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all max-w-[180px]',
          value
            ? 'bg-zinc-100 text-zinc-900 hover:bg-zinc-200'
            : 'bg-zinc-100 text-zinc-400 hover:bg-zinc-200',
        )}
      >
        <span className="truncate">{value ? selectedLabel : (placeholder || 'Select...')}</span>
        <ChevronDown className={cn('size-3 shrink-0 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-50 w-56 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-black/10 overflow-hidden">
          {searchable && (
            <div className="p-1.5 border-b border-zinc-100">
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 text-[11px] font-medium text-zinc-900 bg-zinc-50 rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-zinc-300 placeholder:text-zinc-400"
              />
            </div>
          )}
          <div className="max-h-[240px] overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-[11px] font-medium text-zinc-400">No results</div>
            )}
            {Array.from(groups.entries()).map(([group, items]) => (
              <div key={group}>
                {group && (
                  <div className="px-3 py-1.5 text-[9px] font-black text-zinc-400 uppercase tracking-widest bg-zinc-50 sticky top-0">
                    {group}
                  </div>
                )}
                {items.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onChange(opt.value);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className={cn(
                      'w-full text-left px-3 py-2 text-[11px] font-semibold transition-colors',
                      value === opt.value
                        ? 'bg-zinc-900 text-white'
                        : 'text-zinc-700 hover:bg-zinc-50',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Notes Section
// ---------------------------------------------------------------------------

interface NoteEntry {
  at: string;
  text: string;
  authorName?: string;
}

const NotesSection: React.FC<{
  notes: NoteEntry[];
  onAddNote: (text: string) => Promise<void>;
}> = ({ notes, onAddNote }) => {
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = useCallback(async () => {
    const text = draft.trim();
    if (!text || isSending) return;
    setIsSending(true);
    try {
      await onAddNote(text);
      setDraft('');
    } finally {
      setIsSending(false);
    }
  }, [draft, isSending, onAddNote]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  const formatTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  // Reverse: latest notes first
  const sortedNotes = [...notes].reverse();

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
      <div className="px-3 py-2.5 border-b border-zinc-100 flex items-center gap-2">
        <MessageSquare className="size-3.5 text-zinc-400" />
        <span className={labelCls}>Notes</span>
        {notes.length > 0 && (
          <span className="ml-auto text-[10px] font-bold text-zinc-400">{notes.length}</span>
        )}
      </div>

      {/* Add note input — at top for quick access */}
      <div className="p-2 border-b border-zinc-100">
        <div className="flex gap-1.5">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            className="flex-1 px-2.5 py-1.5 text-[11px] font-medium text-zinc-900 bg-white border border-zinc-200 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-zinc-900 placeholder:text-zinc-400"
            placeholder="Add a note..."
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!draft.trim() || isSending}
            className={cn(
              'px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wide transition-all',
              draft.trim() ? 'bg-zinc-900 text-white hover:bg-zinc-800' : 'bg-zinc-100 text-zinc-400 cursor-not-allowed',
            )}
          >
            {isSending ? <Loader2 className="size-3 animate-spin" /> : 'Post'}
          </button>
        </div>
      </div>

      {/* Notes list — latest first */}
      {sortedNotes.length > 0 && (
        <div className="max-h-[240px] overflow-y-auto px-3 py-2 space-y-2.5">
          {sortedNotes.map((note, idx) => (
            <div key={idx}>
              <div className="flex items-baseline gap-1.5">
                <span className="text-[10px] font-bold text-zinc-900 shrink-0">{note.authorName || 'System'}</span>
                <span className="text-[9px] font-medium text-zinc-400">{formatTime(note.at)}</span>
              </div>
              <p className="text-[11px] font-medium text-zinc-600 mt-0.5 leading-relaxed">{note.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Action Button
// ---------------------------------------------------------------------------

const ActionBtn: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
  variant?: 'primary' | 'secondary' | 'success';
}> = ({ onClick, disabled, icon, label, variant = 'secondary' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'w-full h-9 rounded-xl text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all disabled:opacity-50',
      variant === 'primary' && 'bg-zinc-900 text-white hover:bg-zinc-800',
      variant === 'secondary' && 'bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50 hover:border-zinc-300',
      variant === 'success' && 'bg-emerald-600 text-white hover:bg-emerald-700',
    )}
  >
    {icon}
    {label}
  </button>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ReviewRightPanel: React.FC<ReviewRightPanelProps> = ({
  intakeId,
  firmId,
  onSendEngagementLetter,
  onRefetch,
}) => {
  const formDataCtx = useIntakeFormData();
  const [updateIntakeDirect] = useUpdateIntakeMutation();

  const {
    intake: {
      form,
      status,
      matterId,
      matterStatus,
      handler,
      intakeStatuses,
      handleFieldChange,
      handleStatusChange,
      handleSave,
      selectedFirm,
      handleFirmChange,
      assignedTo,
      handleAssignedToChange,
      handleLeadSourceChange,
      manualQualification,
      companionRoot,
      isCompanionPrimary,
      rejectionEmailSent,
      scheduledAt,
      scheduledAssigneeId,
      scheduledAssigneeName,
      scheduledBackupAssigneeId,
      scheduledBackupAssigneeName,
      googleCalendarEventId,
      googleCalendarEventLink,
      refetch,
      setSummary,
      addGeneralNote,
    },
    exportHelpers: {
      canExport,
      isProcessing: isExportProcessing,
      handleStartExport,
      canDownload,
      handleDownload,
      downloadTimestamp,
    },
    conversion,
    archiveHelpers,
  } = useIntakePage(intakeId, false);

  const contextPhone = formDataCtx?.formData?.personal_info?.phone;
  const livePhone = contextPhone || form.personalInfo.phone;
  const rawFormData = formDataCtx?.intake?.form_data;

  const { data: timelineMeta = {} } = useGetIntakeMetadataQuery(intakeId, { skip: !intakeId });
  const { data: firms = [] } = useGetFirmsQuery({});

  const navigate = useNavigate();
  const [scheduleCallback] = useScheduleCallbackMutation();
  const [cancelSchedule] = useCancelScheduleMutation();
  const [generateIntakeSummary] = useGenerateIntakeSummaryMutation();
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [showPdfMenu, setShowPdfMenu] = useState(false);
  const [getPortalLink] = useLazyGetPortalLinkQuery();

  // ---------------------------------------------------------------------------
  // Handlers that SAVE directly (no stale local state race)
  // ---------------------------------------------------------------------------

  const [portalLinkUrl, setPortalLinkUrl] = useState<string | null>(null);
  const [showPortalLink, setShowPortalLink] = useState(false);

  const handleCopyPortalLink = useCallback(async () => {
    if (!matterId) {
      toast.error('No matter linked to this intake');
      return;
    }
    try {
      const result = await getPortalLink(matterId).unwrap();
      const url = result.portal_url;
      setPortalLinkUrl(url);
      setShowPortalLink(true);
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Secure upload link copied to clipboard');
      } catch {
        // Clipboard API failed (insecure context, permissions, etc.)
        // Popover will show the link for manual copy
      }
    } catch {
      toast.error('Failed to get upload link');
    }
  }, [matterId, getPortalLink]);

  const handleGenerateSummary = useCallback(async () => {
    if (!intakeId) return;
    if (formDataCtx?.isDirty) await formDataCtx.save();
    setIsGeneratingSummary(true);
    try {
      const generated = await generateIntakeSummary(intakeId).unwrap();
      setSummary(generated);
    } finally {
      setIsGeneratingSummary(false);
    }
  }, [intakeId, generateIntakeSummary, setSummary, formDataCtx]);

  const handleGeneratePdf = useCallback(async () => {
    if (formDataCtx?.isDirty) await formDataCtx.save();
    handleStartExport();
  }, [formDataCtx, handleStartExport]);

  const handleLeadSourceWithSave = useCallback(async (value: string) => {
    handleLeadSourceChange(value);
    try {
      await updateIntakeDirect({
        id: intakeId,
        form_data: mergeFormDataPatchWithRepairs(rawFormData, {
          lead_source: value,
        }),
      }).unwrap();
    } catch {
      toast.error('Failed to save lead source');
    }
  }, [intakeId, handleLeadSourceChange, rawFormData, updateIntakeDirect]);

  const handleReferralSourceWithSave = useCallback(async (value: string) => {
    handleFieldChange('referralSource', 'description', value);
    try {
      await updateIntakeDirect({
        id: intakeId,
        form_data: mergeFormDataPatchWithRepairs(rawFormData, {
          referral_source: { description: value },
        }),
      }).unwrap();
    } catch {
      toast.error('Failed to save referral source');
    }
  }, [intakeId, handleFieldChange, rawFormData, updateIntakeDirect]);

  const handleFirmWithSave = useCallback(async (firm: { id: string; name: string } | null) => {
    handleFirmChange(firm);
    try {
      await updateIntakeDirect({ id: intakeId, firm: firm?.id || '' }).unwrap();
    } catch {
      toast.error('Failed to save firm');
    }
  }, [intakeId, handleFirmChange, updateIntakeDirect]);

  const handleAssignedToWithSave = useCallback(async (userId: string | null) => {
    handleAssignedToChange(userId);
    try {
      await updateIntakeDirect({ id: intakeId, assigned_to: userId }).unwrap();
    } catch {
      toast.error('Failed to save assignment');
    }
  }, [intakeId, handleAssignedToChange, updateIntakeDirect]);

  const handleQualificationWithSave = useCallback(async (qual: ManualQualificationEnum | null) => {
    if (!intakeId) return;
    try {
      await updateIntakeDirect({
        id: intakeId,
        manual_qualification: qual,
        skipOptimistic: true,
      }).unwrap();
      const label = qual ? qual.charAt(0).toUpperCase() + qual.slice(1) : 'cleared';
      toast.success(`Qualification ${label}`);
      refetch();
    } catch (err) {
      console.error('Qualification update failed:', err);
      toast.error('Failed to update qualification');
    }
  }, [intakeId, updateIntakeDirect, refetch]);

  // Wrap status tracking changes (LOE status, attorney decision) with direct save
  const handleStatusChangeWithSave = useCallback(async (newStatuses: typeof intakeStatuses) => {
    handleStatusChange(newStatuses); // update local state for UI
    try {
      await updateIntakeDirect({
        id: intakeId,
        skipOptimistic: true,
        form_data: mergeFormDataPatchWithRepairs(rawFormData, {
          loe_status: newStatuses.loeStatus,
          attorney_decision: newStatuses.attorneyDecision,
          rejection_reason: newStatuses.rejectionReason,
        }),
      }).unwrap();
    } catch {
      toast.error('Failed to save status change');
    }
  }, [intakeId, handleStatusChange, rawFormData, updateIntakeDirect]);

  // Local optimistic state for rejection email toggle (server value is slow to reflect)
  const [localRejectionEmailSent, setLocalRejectionEmailSent] = useState<boolean | null>(null);
  const effectiveRejectionEmailSent = localRejectionEmailSent ?? rejectionEmailSent;

  const handleRejectionEmailWithSave = useCallback(async (sent: boolean) => {
    if (!intakeId) return;
    setLocalRejectionEmailSent(sent); // optimistic
    try {
      await updateIntakeDirect({
        id: intakeId,
        rejection_email_sent: sent,
      }).unwrap();
      refetch();
    } catch {
      setLocalRejectionEmailSent(null); // rollback
      toast.error('Failed to update rejection email status');
    }
  }, [intakeId, updateIntakeDirect, refetch]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const notes: NoteEntry[] = (form.generalNotesLog || []).map((n: { at: string; text: string; authorName?: string }) => ({
    at: n.at, text: n.text, authorName: n.authorName,
  }));

  // Referral source helpers
  const referralOptions: PillSelectOption[] = REFERRAL_SOURCES.map((s) => ({
    value: s.value, label: s.label, group: s.group,
  }));
  const referralValue = form.referralSource?.description || '';
  const referralDisplayValue = referralValue.startsWith('Other: ')
    ? 'Other'
    : referralValue.startsWith('Referral Firm - Other: ')
      ? 'Referral Firm - Other'
      : referralValue;
  const isReferralOther = referralDisplayValue === 'Other' || referralDisplayValue === 'Referral Firm - Other';
  const referralCustomText = referralValue.startsWith('Other: ')
    ? referralValue.slice(7)
    : referralValue.startsWith('Referral Firm - Other: ')
      ? referralValue.slice(23)
      : '';

  const firmOptions = firms.map((f: Firm) => ({ id: f.id, name: f.name }));

  // Staff users for assignment dropdown
  const { data: staffUsers = [] } = useGetStaffUsersQuery();
  const staffUserOptions: PillSelectOption[] = staffUsers.map((u) => ({
    value: u.id,
    label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email,
  }));

  // Is intake in chase mode?
  const isChaseMode = status === 'Chase';

  // Sidebar tab state
  const [sidebarTab, setSidebarTab] = useState<'setup' | 'activity' | 'docs'>('setup');

  return (
    <div className="h-full flex flex-col bg-zinc-50">
      {/* Auto Chase + Status + Companion — always visible at top */}
      <div className="shrink-0 p-2.5 pb-0 space-y-1.5">
        {/* Auto Chase — only when status is Chase */}
        {isChaseMode && (
          <div className="[&>div]:!rounded-2xl [&>div]:!border-zinc-200">
            <AutoChaseCard
              intakeId={intakeId}
              firmId={firmId}
              clientName={form.personalInfo?.fullName}
              clientPhone={livePhone}
            />
          </div>
        )}

        <div className="bg-white rounded-2xl border border-zinc-200 px-3 py-2.5 flex items-center justify-between">
          <span className={labelCls}>Status</span>
          <div className="[&_.MuiFormControl-root]:min-w-0 [&_.MuiSelect-select]:!text-[11px] [&_.MuiSelect-select]:!font-bold [&_.MuiSelect-select]:!py-1 [&_.MuiSelect-select]:!px-2 [&_.MuiInputBase-root]:!rounded-lg">
            <StatusSelect intakeId={intakeId} value={status} size="small" hasLinkedLead={conversion.hasLinkedLead} />
          </div>
        </div>

        {/* Companion indicator */}
        {companionRoot && (() => {
          // Get companion rows from form_data (has created_intake_id + client_name)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const companionRows: { client_name?: string; created_intake_id?: string }[] =
            formDataCtx?.formData?.companions ?? [];
          const linkedCompanions = companionRows.filter((r) => r.created_intake_id);

          return (
            <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
              <div className="px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {isCompanionPrimary ? (
                    <Star className="size-3.5 text-amber-500 fill-amber-500" />
                  ) : (
                    <Users className="size-3.5 text-zinc-400" />
                  )}
                  <span className="text-[11px] font-bold text-zinc-900">
                    {isCompanionPrimary ? 'Primary Intake' : 'Companion Intake'}
                  </span>
                </div>
                {!isCompanionPrimary && (
                  <a
                    href={`/intakes/${companionRoot}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] font-bold text-zinc-500 hover:text-zinc-900 underline"
                  >
                    View Primary
                  </a>
                )}
              </div>

              {/* Member list for primary intakes */}
              {isCompanionPrimary && linkedCompanions.length > 0 && (
                <div className="border-t border-zinc-100 px-3 py-1.5 space-y-0.5">
                  {linkedCompanions.map((c, idx) => (
                    <a
                      key={idx}
                      href={`/intakes/${c.created_intake_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between py-1 group"
                    >
                      <div className="flex items-center gap-2">
                        <Users className="size-3 text-zinc-300" />
                        <span className="text-[11px] font-semibold text-zinc-700 group-hover:text-zinc-900">
                          {c.client_name || 'Unnamed'}
                        </span>
                      </div>
                      <span className="text-[9px] font-semibold text-zinc-400 group-hover:text-zinc-600">
                        Open
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Tab bar */}
      <div className="shrink-0 px-2.5 pt-2">
        <div className="flex gap-0.5 bg-zinc-100 rounded-xl p-0.5">
          {(['setup', 'activity', 'docs'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setSidebarTab(tab)}
              className={cn(
                'flex-1 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all',
                sidebarTab === tab
                  ? 'bg-white text-zinc-900 shadow-sm'
                  : 'text-zinc-400 hover:text-zinc-600',
              )}
            >
              {tab === 'setup' ? 'Setup' : tab === 'activity' ? 'Activity' : 'Docs'}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable tab content */}
      <div className="flex-1 overflow-y-auto p-2.5 space-y-2">

      {sidebarTab === 'setup' && (<>
      {/* ================================================================ */}
      {/* SETUP TAB — Status, Qualification, Sources, Assignment, Actions  */}
      {/* ================================================================ */}

      {/* Handler — Finch or Firm */}
      <div className="bg-white rounded-2xl border border-zinc-200 px-3 py-2.5 flex items-center justify-between">
        <span className={labelCls}>Handling</span>
        <HandlerToggle intakeId={intakeId} value={handler} />
      </div>

      {/* Qualification — compact horizontal, direct save */}
      <div className="bg-white rounded-2xl border border-zinc-200 p-3 space-y-2.5">
        <div className="flex items-center justify-between">
          <span className={labelCls}>Qualification</span>
          <div className="flex items-center gap-1">
            {([
              { value: ManualQualificationEnum.QUALIFIED, icon: <CheckCircle2 className="size-3.5" />, active: 'bg-emerald-500 text-white', hover: 'hover:bg-emerald-50 hover:text-emerald-600', label: 'Qualified' },
              { value: ManualQualificationEnum.UNKNOWN, icon: <HelpCircle className="size-3.5" />, active: 'bg-amber-500 text-white', hover: 'hover:bg-amber-50 hover:text-amber-600', label: 'Unknown' },
              { value: ManualQualificationEnum.UNQUALIFIED, icon: <XCircle className="size-3.5" />, active: 'bg-red-500 text-white', hover: 'hover:bg-red-50 hover:text-red-600', label: 'Unqualified' },
            ] as const).map((qual) => {
              const isActive = manualQualification === qual.value;
              return (
                <button
                  key={qual.value}
                  type="button"
                  onClick={() => handleQualificationWithSave(qual.value)}
                  className={cn(
                    'size-7 rounded-lg flex items-center justify-center transition-all',
                    isActive ? qual.active : `bg-zinc-100 text-zinc-400 ${qual.hover}`,
                  )}
                  title={qual.label}
                >
                  {qual.icon}
                </button>
              );
            })}
            {manualQualification && (
              <button
                type="button"
                onClick={() => handleQualificationWithSave(null)}
                className="text-[9px] font-bold text-zinc-400 hover:text-zinc-600 ml-1"
              >
                Reset
              </button>
            )}
          </div>
        </div>
        {/* Rejection details */}
        {intakeStatuses.rejectionReason && (
          <div className="flex items-center justify-between text-[11px]">
            <span className="font-semibold text-zinc-500">Rejection</span>
            <span className="font-bold text-zinc-900 truncate ml-2 max-w-[140px]">{intakeStatuses.rejectionReason}</span>
          </div>
        )}
        {/* Rejection email toggle */}
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-zinc-500">Rejection email sent</span>
          <button
            type="button"
            onClick={() => handleRejectionEmailWithSave(!effectiveRejectionEmailSent)}
            className={cn(
              'w-10 h-5 rounded-full transition-all relative',
              effectiveRejectionEmailSent ? 'bg-zinc-900' : 'bg-zinc-200',
            )}
          >
            <div className={cn(
              'size-3.5 rounded-full bg-white absolute top-[3px] transition-all shadow-sm',
              effectiveRejectionEmailSent ? 'left-[22px]' : 'left-[3px]',
            )} />
          </button>
        </div>
      </div>

      {/* Lead Source + Referral Source — combined, Cash App pill selects, direct saves */}
      <div className="bg-white rounded-2xl border border-zinc-200 p-3 space-y-2.5">
        <div className="flex items-center justify-between">
          <span className={labelCls}>Lead Source</span>
          <PillSelect
            value={form.leadSource}
            options={LEAD_SOURCE_OPTIONS}
            onChange={handleLeadSourceWithSave}
            placeholder="Select..."
          />
        </div>
        <div className="border-t border-zinc-100 pt-2.5">
          <div className="flex items-center justify-between">
            <span className={labelCls}>Referral</span>
            <PillSelect
              value={referralDisplayValue}
              options={referralOptions}
              onChange={(val) => {
                if (val === 'Other' || val === 'Referral Firm - Other') {
                  handleReferralSourceWithSave(val === 'Other' ? 'Other: ' : 'Referral Firm - Other: ');
                } else {
                  handleReferralSourceWithSave(val);
                }
              }}
              placeholder="Select..."
            />
          </div>
          {isReferralOther && (
            <input
              type="text"
              value={referralCustomText}
              onChange={(e) => {
                const prefix = referralDisplayValue === 'Referral Firm - Other' ? 'Referral Firm - Other: ' : 'Other: ';
                handleReferralSourceWithSave(prefix + e.target.value);
              }}
              placeholder={referralDisplayValue === 'Referral Firm - Other' ? 'Firm name...' : 'Details...'}
              className="mt-2 w-full px-2.5 py-1.5 text-[11px] font-semibold text-zinc-900 bg-zinc-50 border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-zinc-900 placeholder:text-zinc-400"
            />
          )}
        </div>
      </div>

      {/* Assignment — searchable PillSelects, direct saves */}
      <div className="bg-white rounded-2xl border border-zinc-200 p-3 space-y-2.5">
        <div className="flex items-center justify-between">
          <span className={labelCls}>Firm</span>
          <PillSelect
            value={selectedFirm?.id || ''}
            options={firmOptions.map((f: { id: string; name: string }) => ({ value: f.id, label: f.name }))}
            onChange={(id) => {
              const firm = firmOptions.find((f: { id: string; name: string }) => f.id === id);
              if (firm) handleFirmWithSave(firm);
            }}
            placeholder="Select firm..."
            searchable
          />
        </div>
        <div className="border-t border-zinc-100 pt-2.5">
          <div className="flex items-center justify-between">
            <span className={labelCls}>Assigned To</span>
            <PillSelect
              value={assignedTo || ''}
              options={staffUserOptions}
              onChange={handleAssignedToWithSave}
              placeholder="Unassigned"
              searchable
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-2xl border border-zinc-200 p-3 space-y-2">
        <span className={labelCls}>Actions</span>

        <ActionBtn
          onClick={handleGenerateSummary}
          disabled={isGeneratingSummary}
          icon={isGeneratingSummary ? <Loader2 className="size-3.5 animate-spin" /> : <Sparkles className="size-3.5" />}
          label={isGeneratingSummary ? 'Generating...' : 'AI Summary'}
          variant="primary"
        />

        {/* PDF — split button */}
        <div className="relative flex">
          <button
            type="button"
            onClick={handleGeneratePdf}
            disabled={isExportProcessing || !canExport}
            className={cn(
              'flex-1 h-9 text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all disabled:opacity-50',
              'bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50',
              canDownload ? 'rounded-l-xl border-r-0' : 'rounded-xl',
            )}
          >
            {isExportProcessing
              ? <><Loader2 className="size-3.5 animate-spin" /> Generating...</>
              : <><FileText className="size-3.5" /> Generate PDF</>
            }
          </button>
          {canDownload && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowPdfMenu(!showPdfMenu)}
                className="h-9 px-2 rounded-r-xl border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 transition-all flex items-center"
              >
                <ChevronDown className={cn('size-3.5 transition-transform', showPdfMenu && 'rotate-180')} />
              </button>
              {showPdfMenu && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-black/10 overflow-hidden w-48">
                  <button
                    type="button"
                    onClick={() => { handleDownload(); setShowPdfMenu(false); }}
                    className="w-full text-left px-3 py-2 text-[11px] font-bold text-zinc-700 hover:bg-zinc-50 flex items-center gap-2"
                  >
                    <FileText className="size-3.5 text-zinc-500" />
                    <div className="flex flex-col">
                      <span>View Generated PDF</span>
                      {downloadTimestamp && (
                        <span className="text-[9px] font-medium text-zinc-500">
                          {new Date(downloadTimestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {onSendEngagementLetter && (
          <ActionBtn onClick={onSendEngagementLetter} icon={<Send className="size-3.5" />} label="Send Engagement Letter" />
        )}

        {matterId && (
          <Popover open={showPortalLink} onOpenChange={setShowPortalLink}>
            <PopoverTrigger asChild>
              <button type="button" onClick={handleCopyPortalLink} className={cn(
                'w-full h-9 rounded-xl text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all',
                'bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50 hover:border-zinc-300',
              )}>
                <Link2 className="size-3.5" />
                Copy Secure Upload Link
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 p-3" onOpenAutoFocus={(e) => e.preventDefault()}>
              <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1.5">Upload Link</p>
              {portalLinkUrl ? (
                <input
                  readOnly
                  value={portalLinkUrl}
                  onFocus={(e) => e.target.select()}
                  className="w-full text-xs font-mono bg-zinc-50 border border-zinc-200 rounded-lg px-2.5 py-2 text-zinc-700 select-all"
                />
              ) : (
                <div className="flex items-center gap-2 py-2 text-xs text-zinc-400">
                  <Loader2 className="size-3 animate-spin" />
                  Generating link...
                </div>
              )}
            </PopoverContent>
          </Popover>
        )}

        {/* Matter — grouped horizontal buttons */}
        {(() => {
          // Matter is "activated" when it exists and has moved past Intake status
          const isActivated = Boolean(matterId && matterStatus !== 'Intake');
          // canConvert checks lead/referral source requirements
          const canActivate = !isActivated;
          const isMissingRequirements = !conversion.canConvert;

          return (
            <div className="flex gap-0">
              <button
                type="button"
                onClick={() => canActivate ? conversion.handleConvertToMatter() : undefined}
                disabled={isActivated || conversion.isConverting}
                className={cn(
                  'flex-1 h-9 rounded-l-xl border border-r-0 text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all',
                  isActivated
                    ? 'bg-zinc-50 text-zinc-400 cursor-default border-zinc-200'
                    : 'bg-zinc-900 text-white hover:bg-zinc-800 border-zinc-900 disabled:opacity-50',
                )}
              >
                {conversion.isConverting ? (
                  <><Loader2 className="size-3.5 animate-spin" /> Activating...</>
                ) : isActivated ? (
                  <><CheckCircle2 className="size-3.5" /> Activated</>
                ) : (
                  <><Zap className="size-3.5" /> Activate Matter</>
                )}
              </button>
              <button
                type="button"
                onClick={() => matterId && navigate(`/matters/${matterId}`)}
                disabled={!matterId}
                className={cn(
                  'h-9 px-3 rounded-r-xl border text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all',
                  matterId
                    ? 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50 cursor-pointer'
                    : 'bg-zinc-50 border-zinc-200 text-zinc-300 cursor-not-allowed',
                )}
              >
                <Eye className="size-3.5" />
                View Matter
              </button>
            </div>
          );
        })()}

        {/* Archive */}
        <ActionBtn
          onClick={archiveHelpers.handleArchiveToggle}
          disabled={archiveHelpers.isArchiving}
          icon={archiveHelpers.isArchiving ? <Loader2 className="size-3.5 animate-spin" /> : <Archive className="size-3.5" />}
          label={archiveHelpers.isArchived ? 'Unarchive Intake' : 'Archive Intake'}
        />
      </div>

      {/* Intake Progress (LOE + Attorney Decision) */}
      {cssOverride(
        <StatusTrackingPanel
          intakeId={intakeId}
          statuses={intakeStatuses}
          onStatusChange={handleStatusChangeWithSave}
          clientContactInfo={{ phone: livePhone }}
          onSendEngagementLetter={onSendEngagementLetter ? () => onSendEngagementLetter() : () => {}}
          intakeData={form}
          onSendSuccess={refetch}
          onSave={handleSave}
        />
      )}

      {/* Metadata — subtle at bottom */}
      <div className="pt-1 pb-2 px-1 space-y-1">
        {[
          { label: 'Created', value: timelineMeta.intake_created_at },
          { label: 'Matter', value: timelineMeta.matter_created_at },
          { label: 'LOE Sent', value: timelineMeta.loe_sent_at },
          { label: 'LOE Signed', value: timelineMeta.loe_signed_at },
          { label: 'Updated', value: timelineMeta.intake_updated_at },
        ].filter((m): m is { label: string; value: string } => !!m.value).map((m) => (
          <div key={m.label} className="flex items-center justify-between">
            <span className="text-[9px] font-semibold text-zinc-400">{m.label}</span>
            <span className="text-[9px] font-medium text-zinc-400">
              {new Date(m.value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
        ))}
      </div>
      </>)}

      {sidebarTab === 'activity' && (<>
      {/* ================================================================ */}
      {/* ACTIVITY TAB — Chase, Notes, Schedule Callback                   */}
      {/* ================================================================ */}


      {/* Schedule Callback */}
      {cssOverride(
        <ScheduleCallbackPanel
          clientState={form.incidentInfo?.state || ''}
          intakeAssignedTo={assignedTo}
          scheduledAt={scheduledAt}
          scheduledAssigneeName={scheduledAssigneeName}
          scheduledBackupAssigneeName={scheduledBackupAssigneeName}
          scheduledAssigneeId={scheduledAssigneeId}
          scheduledBackupAssigneeId={scheduledBackupAssigneeId}
          googleCalendarEventId={googleCalendarEventId}
          googleCalendarEventLink={googleCalendarEventLink}
          onSchedule={async (data) => {
            await scheduleCallback({ intakeId, ...data } as ScheduleCallbackPayload).unwrap();
          }}
          onCancel={async () => {
            await cancelSchedule(intakeId).unwrap();
          }}
        />
      )}

      {/* Notes */}
      <NotesSection notes={notes} onAddNote={addGeneralNote} />
      </>)}

      {sidebarTab === 'docs' && (<>
      {/* ================================================================ */}
      {/* DOCS TAB — Document upload and management                        */}
      {/* ================================================================ */}

      {matterId ? (
        <div className="space-y-2">
          {/* Download All header */}
          <DocsHeader matterId={matterId} />
          {/* File list with upload */}
          <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden" style={{ height: 'calc(100% - 48px)' }}>
            <DocumentsPanel matterId={matterId} />
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-zinc-200 p-6">
          <div className="text-center py-6">
            <div className="size-10 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-3">
              <FileText className="size-5 text-zinc-400" />
            </div>
            <p className="text-xs font-bold text-zinc-900">Documents Available After Matter Activation</p>
            <p className="text-[11px] font-medium text-zinc-500 mt-1 leading-relaxed">
              Activate a matter from the Setup tab to start uploading and managing documents.
            </p>
          </div>
        </div>
      )}
      </>)}

      </div>
    </div>
  );
};

export default ReviewRightPanel;
